/**
 * PLW single-source overlay tabs — pulls page content into overlay panels so
 * copy is entered ONCE (on the page) and mirrored into a PDP slide-out.
 *
 *   <plw-page-tabs data-page-url="/pages/shipping-returns">
 *     …tablist…
 *     <div role="tabpanel">…<div data-plw-pdp-panel="shipping">fallback</div>…</div>
 *     <div role="tabpanel">…<div data-plw-pdp-panel="returns">fallback</div>…</div>
 *   </plw-page-tabs>
 *
 * Fetches the rendered page at data-page-url ON IDLE AFTER CONNECT — before
 * the overlay is ever opened, so the swap is never visible (client QA
 * 2026-08-18) — and replaces each [data-plw-pdp-panel="{name}"]'s children
 * with the page's [data-plw-pdp-tab="{name}"] blocks (the attribute is emitted
 * by snippets/plw-basic-block.liquid for blocks whose `pdp_tab` setting opts
 * in). The host overlay's dialog:open stays wired purely as a RETRY hook: a
 * failed prefetch re-arms, so opening tries again. A panel whose name matches
 * no tagged block keeps its server-rendered fallback, as does everything on
 * fetch failure. JS-off keeps the fallback markup — the panels are complete
 * without this element.
 */
class PlwPageTabs extends HTMLElement {
  #loaded = false;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Element | null} */
  #overlay = null;

  connectedCallback() {
    this.#overlay = this.closest('plw-overlay');
    this.#overlay?.addEventListener('dialog:open', this.#load);
    // Prefetch once the main work is done; setTimeout fallback for Safari.
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.#load());
    } else {
      setTimeout(this.#load, 1);
    }
  }

  disconnectedCallback() {
    this.#overlay?.removeEventListener('dialog:open', this.#load);
    this.#abortController?.abort();
  }

  #load = async () => {
    if (this.#loaded || !this.isConnected || !this.dataset.pageUrl) return;
    this.#loaded = true;

    try {
      this.#abortController = new AbortController();
      const response = await fetch(this.dataset.pageUrl, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Page fetch failed (${response.status})`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      for (const panel of this.querySelectorAll('[data-plw-pdp-panel]')) {
        const name = panel.getAttribute('data-plw-pdp-panel') || '';
        const blocks = doc.querySelectorAll(`[data-plw-pdp-tab="${CSS.escape(name)}"]`);
        if (!blocks.length) continue;
        panel.replaceChildren(...[...blocks].map((block) => document.importNode(block, true)));
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
      // Keep the server-rendered fallback and retry on the next open.
      this.#loaded = false;
    }
  };
}

if (!customElements.get('plw-page-tabs')) customElements.define('plw-page-tabs', PlwPageTabs);
